import * as Dialog from "@radix-ui/react-dialog";
import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type ReactNode,
  type InputHTMLAttributes,
} from "react";
import { useTranslation } from "react-i18next";
import { AlertCircle, ArrowRight, LoaderCircle, X } from "lucide-react";
import { friendlyError } from "../core/i18n";
export function Button({
  children,
  busy,
  secondary = false,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  busy?: boolean;
  secondary?: boolean;
}) {
  return (
    <button
      {...props}
      disabled={props.disabled || busy}
      className={`button ${secondary ? "secondary" : ""} ${props.className || ""}`}
      aria-busy={busy || undefined}
    >
      {busy ? <LoaderCircle className="spin" size={20} /> : null}
      {children}
    </button>
  );
}
export function Next({
  children,
  busy,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  busy?: boolean;
  secondary?: boolean;
}) {
  return (
    <Button {...props} busy={busy}>
      {children}
      {!busy && <ArrowRight size={20} />}
    </Button>
  );
}
export function Actions({ children }: { children: ReactNode }) {
  const dock = useRef<HTMLDivElement>(null),
    space = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const element = dock.current;
    if (!element) return;
    const observer = new ResizeObserver(() =>
      space.current?.style.setProperty(
        "--action-dock-height",
        `${element.getBoundingClientRect().height}px`,
      ),
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  return (
    <div ref={space} className="action-space">
      <div ref={dock} className="action-dock">
        <div>{children}</div>
      </div>
    </div>
  );
}
export function Heading({
  eyebrow,
  title,
  lead,
  icon,
}: {
  eyebrow?: string;
  title: string;
  lead?: string;
  icon?: ReactNode;
}) {
  const heading = useRef<HTMLHeadingElement>(null);
  useLayoutEffect(() => {
    heading.current?.focus({ preventScroll: true });
  }, []);
  return (
    <header className="page-heading">
      {eyebrow && <p className="eyebrow">{eyebrow}</p>}
      {icon && <div className="heading-icon">{icon}</div>}
      <h1 ref={heading} tabIndex={-1}>
        {title}
      </h1>
      {lead && <p className="lead">{lead}</p>}
    </header>
  );
}
export function ErrorPanel({
  error,
  retry,
}: {
  error: string | null;
  retry?: () => void;
}) {
  const { t } = useTranslation();
  if (!error) return null;
  return (
    <div className="notice error" role="alert">
      <AlertCircle size={20} />
      <div>
        <p>{friendlyError(error)}</p>
        {retry && (
          <button className="text-button" onClick={retry}>
            {t("ui.retry")}
          </button>
        )}
      </div>
    </div>
  );
}
export function Field({
  label,
  error,
  hint,
  id,
  ...props
}: InputHTMLAttributes<HTMLInputElement> & {
  label: string;
  error?: string;
  hint?: string;
}) {
  const fieldId = id || props.name || label;
  return (
    <div className="field">
      <label htmlFor={fieldId}>{label}</label>
      <input
        {...props}
        id={fieldId}
        aria-invalid={!!error}
        aria-describedby={
          error ? `${fieldId}-error` : hint ? `${fieldId}-hint` : undefined
        }
      />
      {hint && (
        <p className="hint" id={`${fieldId}-hint`}>
          {hint}
        </p>
      )}
      {error && (
        <p className="field-error" id={`${fieldId}-error`}>
          {error}
        </p>
      )}
    </div>
  );
}
export function Loading() {
  const { t } = useTranslation();
  const [slow, setSlow] = useState(false);
  useEffect(() => {
    const id = setTimeout(() => setSlow(true), 8000);
    return () => clearTimeout(id);
  }, []);
  return (
    <div className="loading" role="status">
      <LoaderCircle className="spin" />
      <p>{t(slow ? "ui.slow" : "ui.loading")}</p>
    </div>
  );
}
export function Modal({
  open,
  title,
  children,
  onClose,
}: {
  open: boolean;
  title: string;
  children: ReactNode;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const previousFocus = useRef<HTMLElement | null>(null);
  return (
    <Dialog.Root
      open={open}
      onOpenChange={(value) => {
        if (!value) onClose();
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="dialog-overlay" />
        <Dialog.Content
          className="dialog"
          aria-describedby={undefined}
          onOpenAutoFocus={() => {
            previousFocus.current = document.activeElement as HTMLElement;
          }}
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            if (document.activeElement === document.body)
              previousFocus.current?.focus();
          }}
          onPointerDownOutside={(e) => e.preventDefault()}
        >
          <div className="dialog-head">
            <Dialog.Title>{title}</Dialog.Title>
            <Dialog.Close asChild>
              <button
                type="button"
                className="icon-button"
                aria-label={t("ui.close")}
              >
                <X />
              </button>
            </Dialog.Close>
          </div>
          {children}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
export function useTask() {
  const [busy, setBusy] = useState(false),
    [error, setError] = useState<string | null>(null);
  const lock = useRef(false),
    alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  async function run(task: () => Promise<void>) {
    if (lock.current) return;
    lock.current = true;
    setBusy(true);
    setError(null);
    try {
      await task();
    } catch (e) {
      if (alive.current)
        setError(
          (e as { code?: string })?.code ||
            (e instanceof Error ? e.message : "generic"),
        );
    } finally {
      lock.current = false;
      if (alive.current) setBusy(false);
    }
  }
  return { busy, error, run, setError };
}
